import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../js/hooks/useAuth";
import { hasAgentAccess, hasBrokerAccess } from "../../js/auth/authService";
import {
  supportUrl,
  manualsUrl,
  journeyUrl,
  journeyOrigin,
  JOURNEY_LINK_PROPS,
} from "../../js/utils/siteLinks";
import { useStore } from "../../js/state/store";
import { resolveOfficeContact } from "../../js/api/officeContact";
import { openSampleArchivePdf, openSampleReportPdf, openSampleSaleClosePdf } from "../../js/api/samplePdf";
import { useClientPortal } from "../../js/hooks/useClientPortal";
import FooterReturnTop from "./FooterReturnTop";
import ReMark from "../brand/ReMark";
import { CAC_TITLE, REPCSP_TITLE } from "../../js/utils/reptc";

function ContactCard({ data }) {
  if (!data) return null;
  const locations = String(data.locations || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className="footer-contact-card">
      <p className="footer-col__eyebrow">{data.heading}</p>
      {data.office_name ? <p className="footer-brands__title">{data.office_name}</p> : null}
      {data.name ? (
        <p className="footer-broker__block">
          {data.name}
          {data.title ? ` · ${data.title}` : ""}
        </p>
      ) : null}
      {data.address ? (
        <p className="footer-broker__block" style={{ whiteSpace: "pre-line" }}>
          {data.address}
        </p>
      ) : null}
      {data.phone ? <p className="footer-broker__block">{data.phone}</p> : null}
      {data.email ? (
        <p className="footer-broker__block">
          <a href={`mailto:${data.email}`}>{data.email}</a>
        </p>
      ) : null}
      {data.website ? (
        <p className="footer-broker__block">
          <a href={data.website} target="_blank" rel="noreferrer">
            {data.website.replace(/^https?:\/\//, "")}
          </a>
        </p>
      ) : null}
      {locations.length ? (
        <ul className="footer-broker__locations">
          {locations.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {data.license ? <p className="footer-broker__block">License {data.license}</p> : null}
      {data.brokerage_license ? (
        <p className="footer-broker__block">Brokerage license {data.brokerage_license}</p>
      ) : null}
      {data.license_expires ? <p className="footer-broker__block">Expires {data.license_expires}</p> : null}
      {data.extra ? <p className="footer-broker__block" style={{ whiteSpace: "pre-line" }}>{data.extra}</p> : null}
    </div>
  );
}

export default function Footer() {
  const { session, role } = useAuth();
  const { dispatch } = useStore();
  const navigate = useNavigate();
  const contacts = resolveOfficeContact(session);
  const portal = useClientPortal();
  const hidePro = Boolean(portal.active);

  function openOffice() {
    if (role === "admin") {
      navigate("/admin");
      return;
    }
    if (hasBrokerAccess(role)) {
      navigate("/broker/dashboard");
      return;
    }
    if (hasAgentAccess(role)) {
      navigate("/agent/dashboard");
      return;
    }
    dispatch({ type: "SET_PENDING_PATH", payload: "" });
    dispatch({ type: "OPEN_MODAL", payload: "officeLogin" });
  }

  return (
    <footer className="site-footer" id="information-resources" data-tour="footer">
      <div className="footer-columns">
        <div className="footer-col footer-col--resources" data-tour="footer-resources">
          <p className="footer-col__eyebrow">Information & Resources</p>
          <nav className="footer-nav" aria-label="Information and resources">
            {hidePro ? null : (
              <button type="button" className="footer-admin-link" onClick={() => dispatch({ type: "OPEN_MODAL", payload: "systemInfo" })}>
                About
              </button>
            )}
            <Link to="/contact">Contact</Link>
            <a href={supportUrl(session)} {...JOURNEY_LINK_PROPS}>
              Support
            </a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/eula">EULA</Link>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/notices">Notices</Link>
            {role === "admin" ? (
              <>
                <Link to="/admin">Admin</Link>
                <button type="button" className="footer-admin-link" onClick={openSampleReportPdf}>
                  Sample Report
                </button>
                <button type="button" className="footer-admin-link" onClick={openSampleArchivePdf}>
                  Sample Archive
                </button>
                <button type="button" className="footer-admin-link" onClick={openSampleSaleClosePdf}>
                  Sample Close
                </button>
              </>
            ) : null}
          </nav>
          {hidePro ? null : (
            <div className="footer-office-wrap">
              <button type="button" className="footer-office" onClick={openOffice}>
                Office
              </button>
              <button type="button" className="footer-pro-login" onClick={openOffice}>
                Professional Login
              </button>
            </div>
          )}
        </div>

        <div className="footer-col footer-col--site" data-tour="footer-pages">
          <p className="footer-col__eyebrow">Full Site Links</p>
          <nav className="footer-nav" aria-label="Full site">
            <Link to="/">Home</Link>
            <button
              type="button"
              className="footer-admin-link"
              onClick={() => dispatch({ type: "OPEN_MODAL", payload: "clientAccess" })}
            >
              Client Access
            </button>
            {hidePro ? null : <Link to="/dashboard">Dashboard</Link>}
            {hidePro ? null : (
              <>
                {role === "buyer" ? (
                  <a href={manualsUrl("buyer", session)} {...JOURNEY_LINK_PROPS}>
                    Buyer Guide
                  </a>
                ) : null}
                {role === "seller" ? (
                  <a href={manualsUrl("seller", session)} {...JOURNEY_LINK_PROPS}>
                    Seller Guide
                  </a>
                ) : null}
                {role === "agent" ? (
                  <a href={manualsUrl("agent", session)} {...JOURNEY_LINK_PROPS}>
                    Agent Guide
                  </a>
                ) : null}
                {role === "broker" ? (
                  <a href={manualsUrl("broker", session)} {...JOURNEY_LINK_PROPS}>
                    Broker Guide
                  </a>
                ) : null}
                {role === "admin" ? (
                  <>
                    <a href={manualsUrl("buyer", session)} {...JOURNEY_LINK_PROPS}>
                      Buyer Guide
                    </a>
                    <a href={manualsUrl("seller", session)} {...JOURNEY_LINK_PROPS}>
                      Seller Guide
                    </a>
                    <a href={manualsUrl("agent", session)} {...JOURNEY_LINK_PROPS}>
                      Agent Guide
                    </a>
                    <a href={manualsUrl("broker", session)} {...JOURNEY_LINK_PROPS}>
                      Broker Guide
                    </a>
                  </>
                ) : null}
                <Link to="/start">Buyer Journey</Link>
                <Link to="/seller/home">Seller Home</Link>
                <Link to="/agent/dashboard">Agent Desk</Link>
                <Link to="/broker/dashboard">Broker Desk</Link>
                {hasAgentAccess(role) || hasBrokerAccess(role) || role === "admin" ? (
                  <Link to="/account">Account</Link>
                ) : null}
              </>
            )}
            <a href="/downloads/REChecklist-Setup.exe" download>
              Install Windows App
            </a>
          </nav>
        </div>

        <div className="footer-col footer-col--contact" data-tour="footer-contact">
          <p className="footer-col__eyebrow">{hidePro ? "Your advisor" : "Agent / Broker Contact & License"}</p>
          {contacts.length ? (
            contacts.map((card) => <ContactCard key={`${card.kind}-${card.name}`} data={card} />)
          ) : (
            <p className="footer-broker__block">
              Office name, phone, email, and license appear here from the Agent or Broker profile —
              not from a separate footer form.
            </p>
          )}
          <p className="footer-col__note">
            License numbers are recorded by the office. This is not a California DRE certification.
          </p>
        </div>

        <div className="footer-brands-stack">
          <div className="footer-col footer-col--brand">
            <p className="footer-brands__title">DreamHome Journey</p>
            <a href={session ? journeyUrl("/", session) : journeyOrigin()} {...JOURNEY_LINK_PROPS}>
              dreamhomejourney.online
            </a>
          </div>
          <div className="footer-col footer-col--brand">
            <p className="footer-brands__title">
              <ReMark />
            </p>
            <a href="https://rechecklist.pro" target="_blank" rel="noreferrer">
              rechecklist.pro
            </a>
            <a href="mailto:marketing@rechecklist.pro">marketing@rechecklist.pro</a>
          </div>
          <div className="footer-col footer-col--brand footer-col--tophat">
            <p className="footer-brands__title">TopHat VIP</p>
            <a href="https://tophatvip.com" target="_blank" rel="noreferrer">
              tophatvip.com
            </a>
            <p className="footer-col__tagline">Executive Luxury Transportation</p>
          </div>
        </div>
      </div>
      {hidePro ? null : (
        <p className="footer-reptc">
          {REPCSP_TITLE}
          <span>{CAC_TITLE}</span>
        </p>
      )}
      <p className="footer-copy">
        © 2026 REChecklist - DreamHome Journey Executive Real Estate Tools - Digital Solutions Group llp
      </p>
      <FooterReturnTop />
    </footer>
  );
}
